import { useMutation, useQuery } from "@apollo/client";
import Add from "@mui/icons-material/Add";
import Close from "@mui/icons-material/Close";
import ExpandMore from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { SuggestedField } from "components/ArtificialIntelligence/SuggestedField";
import { SuggestedTextField } from "components/ArtificialIntelligence/SuggestedTextField";
import { DocumentDialogContainer } from "components/Document/shared/DocumentDialogContainer";
import { safeParseFloat, safeParseInt } from "encamp-shared/src/tierii/utilities";
import { gql } from "generated-graphql";
import GET_DOCUMENT_DOWNLOAD_LINK_QUERY from "queries/getDocumentDownloadLink";
import { useCallback, useMemo, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";

const DOCUMENT_MANIFEST_DETAIL_QUERY = gql(`
  query DocumentManifestDetail($wasteManifestId: ID!) {
    documentManifestDetail(wasteManifestId: $wasteManifestId) {
      wasteManifest {
        id
        manifestTrackingNumber
        shippedDate
        receivedDate
        generatorId
        generatorName
        generatorMailStreet1
        generatorMailStreet2
        generatorMailCity
        generatorMailState
        generatorMailZip
        generatorLocationStreet1
        generatorLocationStreet2
        generatorLocationCity
        generatorLocationState
        generatorLocationZip
        desFacilityId
        desFacilityName
        desFacLocationStreet1
        desFacLocationCity
        desFacLocationState
        desFacLocationZip
        sourceDocument {
          documentMetadata {
            needsReview
          }
        }
      }
      wasteLines {
        wasteLineNumber
        dotHazardous
        dotIdNumberDescription
        dotPrintedInformation
        nonHazWasteDescription
        containerNumber
        containerTypeCode
        quantity
        qtyUnitOfMeasureCode
        managementMethodCode
        federalWasteCodes
        stateWasteCodes
      }
      wasteTransporters {
        transporterLineNumber
        transporterId
        transporterName
      }
    }
  }
`);

const EDIT_DOCUMENT_MANIFEST_MUTATION = gql(`
  mutation EditDocumentManifest($wasteManifestId: ID!, $input: DocumentManifestEditInput!) {
    editDocumentManifest(wasteManifestId: $wasteManifestId, input: $input) {
      wasteManifest {
        id
        manifestTrackingNumber
      }
    }
  }
`);

type FormValues = {
  manifestTrackingNumber: string;
  shippedDate: string;
  receivedDate: string;
  generatorId: string;
  generatorName: string;
  generatorMailStreet1: string;
  generatorMailStreet2: string;
  generatorMailCity: string;
  generatorMailState: string;
  generatorMailZip: string;
  generatorLocationStreet1: string;
  generatorLocationStreet2: string;
  generatorLocationCity: string;
  generatorLocationState: string;
  generatorLocationZip: string;
  desFacilityId: string;
  desFacilityName: string;
  desFacLocationStreet1: string;
  desFacLocationCity: string;
  desFacLocationState: string;
  desFacLocationZip: string;
  wasteLines: {
    wasteLineNumber: string;
    dotHazardous: boolean;
    dotIdNumberDescription: string;
    dotPrintedInformation: string;
    nonHazWasteDescription: string;
    containerNumber: number | null;
    containerTypeCode: string;
    quantity: number | null;
    qtyUnitOfMeasureCode: string;
    managementMethodCode: string;
    federalWasteCodes: string;
    stateWasteCodes: string;
  }[];
  transporters: {
    transporterLineNumber: string;
    transporterId: string;
    transporterName: string;
  }[];
};

type DocumentManifestEditDialogProps = {
  open: boolean;
  onClose: () => void;
  wasteManifestId: string;
  sourceDocumentId?: string;
};

function formatDateForInput(dateStr: string | null | undefined): string {
  if (!dateStr) return "";
  try {
    return new Date(dateStr).toISOString().slice(0, 10);
  } catch {
    return "";
  }
}

export function DocumentManifestEditDialog({
  open,
  onClose,
  wasteManifestId,
  sourceDocumentId,
}: DocumentManifestEditDialogProps) {
  const alerts = useAlerts();

  const { data: downloadLinkData } = useQuery(GET_DOCUMENT_DOWNLOAD_LINK_QUERY, {
    variables: { id: sourceDocumentId ?? "" },
    skip: !sourceDocumentId || !open,
  });
  const previewUrl = useMemo(
    () => downloadLinkData?.getDocumentDownloadLink ?? undefined,
    [downloadLinkData?.getDocumentDownloadLink]
  );

  const { data, loading } = useQuery(DOCUMENT_MANIFEST_DETAIL_QUERY, {
    variables: { wasteManifestId },
    skip: !wasteManifestId || !open,
    fetchPolicy: "network-only",
  });

  const [editMutation, { loading: saving }] = useMutation(EDIT_DOCUMENT_MANIFEST_MUTATION, {
    refetchQueries: ["ProcessedManifests", "DocumentManifestDetail"],
  });

  const formValues = useMemo((): FormValues => {
    const wasteManifest = data?.documentManifestDetail?.wasteManifest;
    const wasteLines = data?.documentManifestDetail?.wasteLines ?? [];
    const wasteTransporters = data?.documentManifestDetail?.wasteTransporters ?? [];

    return {
      manifestTrackingNumber: wasteManifest?.manifestTrackingNumber ?? "",
      shippedDate: formatDateForInput(wasteManifest?.shippedDate),
      receivedDate: formatDateForInput(wasteManifest?.receivedDate),
      generatorId: wasteManifest?.generatorId ?? "",
      generatorName: wasteManifest?.generatorName ?? "",
      generatorMailStreet1: wasteManifest?.generatorMailStreet1 ?? "",
      generatorMailStreet2: wasteManifest?.generatorMailStreet2 ?? "",
      generatorMailCity: wasteManifest?.generatorMailCity ?? "",
      generatorMailState: wasteManifest?.generatorMailState ?? "",
      generatorMailZip: wasteManifest?.generatorMailZip ?? "",
      generatorLocationStreet1: wasteManifest?.generatorLocationStreet1 ?? "",
      generatorLocationStreet2: wasteManifest?.generatorLocationStreet2 ?? "",
      generatorLocationCity: wasteManifest?.generatorLocationCity ?? "",
      generatorLocationState: wasteManifest?.generatorLocationState ?? "",
      generatorLocationZip: wasteManifest?.generatorLocationZip ?? "",
      desFacilityId: wasteManifest?.desFacilityId ?? "",
      desFacilityName: wasteManifest?.desFacilityName ?? "",
      desFacLocationStreet1: wasteManifest?.desFacLocationStreet1 ?? "",
      desFacLocationCity: wasteManifest?.desFacLocationCity ?? "",
      desFacLocationState: wasteManifest?.desFacLocationState ?? "",
      desFacLocationZip: wasteManifest?.desFacLocationZip ?? "",
      wasteLines: wasteLines.map((wl) => ({
        wasteLineNumber: wl.wasteLineNumber,
        dotHazardous: wl.dotHazardous ?? false,
        dotIdNumberDescription: wl.dotIdNumberDescription ?? "",
        dotPrintedInformation: wl.dotPrintedInformation ?? "",
        nonHazWasteDescription: wl.nonHazWasteDescription ?? "",
        containerNumber: wl.containerNumber ?? null,
        containerTypeCode: wl.containerTypeCode ?? "",
        quantity: wl.quantity ?? null,
        qtyUnitOfMeasureCode: wl.qtyUnitOfMeasureCode ?? "",
        managementMethodCode: wl.managementMethodCode ?? "",
        federalWasteCodes: (wl.federalWasteCodes ?? []).join(", "),
        stateWasteCodes: (wl.stateWasteCodes ?? []).join(", "),
      })),
      transporters: wasteTransporters.map((t) => ({
        transporterLineNumber: t.transporterLineNumber,
        transporterId: t.transporterId ?? "",
        transporterName: t.transporterName ?? "",
      })),
    };
  }, [data]);

  const form = useForm<FormValues>({
    values: formValues,
  });
  const { control, handleSubmit, watch } = form;

  const needsReview = useMemo(
    () =>
      data?.documentManifestDetail?.wasteManifest?.sourceDocument?.documentMetadata?.needsReview ===
      true,
    [data]
  );

  const originalTrackingNumber =
    data?.documentManifestDetail?.wasteManifest.manifestTrackingNumber ?? "";
  const currentTrackingNumber = watch("manifestTrackingNumber");
  const trackingNumberChanged =
    !!originalTrackingNumber &&
    !!currentTrackingNumber &&
    currentTrackingNumber !== originalTrackingNumber;

  const {
    fields: wasteLineFields,
    append: appendWasteLine,
    remove: removeWasteLine,
  } = useFieldArray({ control, name: "wasteLines" });

  const {
    fields: transporterFields,
    append: appendTransporter,
    remove: removeTransporter,
  } = useFieldArray({ control, name: "transporters" });

  const [deletedWasteLineNumbers, setDeletedWasteLineNumbers] = useState<string[]>([]);
  const [deletedTransporterLineNumbers, setDeletedTransporterLineNumbers] = useState<string[]>([]);

  const onSubmit = useCallback(
    async (values: FormValues) => {
      try {
        await editMutation({
          variables: {
            wasteManifestId,
            input: {
              manifestTrackingNumber: values.manifestTrackingNumber || null,
              shippedDate: values.shippedDate || null,
              receivedDate: values.receivedDate || null,
              generatorId: values.generatorId || null,
              generatorName: values.generatorName || null,
              generatorMailStreet1: values.generatorMailStreet1 || null,
              generatorMailStreet2: values.generatorMailStreet2 || null,
              generatorMailCity: values.generatorMailCity || null,
              generatorMailState: values.generatorMailState || null,
              generatorMailZip: values.generatorMailZip || null,
              generatorLocationStreet1: values.generatorLocationStreet1 || null,
              generatorLocationStreet2: values.generatorLocationStreet2 || null,
              generatorLocationCity: values.generatorLocationCity || null,
              generatorLocationState: values.generatorLocationState || null,
              generatorLocationZip: values.generatorLocationZip || null,
              desFacilityId: values.desFacilityId || null,
              desFacilityName: values.desFacilityName || null,
              desFacLocationStreet1: values.desFacLocationStreet1 || null,
              desFacLocationCity: values.desFacLocationCity || null,
              desFacLocationState: values.desFacLocationState || null,
              desFacLocationZip: values.desFacLocationZip || null,
              wasteLines: values.wasteLines.map((wl) => ({
                wasteLineNumber: wl.wasteLineNumber,
                dotHazardous: wl.dotHazardous,
                dotIdNumberDescription: wl.dotIdNumberDescription || null,
                dotPrintedInformation: wl.dotPrintedInformation || null,
                nonHazWasteDescription: wl.nonHazWasteDescription || null,
                containerNumber: wl.containerNumber,
                containerTypeCode: wl.containerTypeCode || null,
                quantity: wl.quantity,
                qtyUnitOfMeasureCode: wl.qtyUnitOfMeasureCode || null,
                managementMethodCode: wl.managementMethodCode || null,
                federalWasteCodes: wl.federalWasteCodes
                  ? wl.federalWasteCodes
                      .split(",")
                      .map((c) => c.trim())
                      .filter(Boolean)
                  : [],
                stateWasteCodes: wl.stateWasteCodes
                  ? wl.stateWasteCodes
                      .split(",")
                      .map((c) => c.trim())
                      .filter(Boolean)
                  : [],
              })),
              transporters: values.transporters.map((t) => ({
                transporterLineNumber: t.transporterLineNumber,
                transporterId: t.transporterId || null,
                transporterName: t.transporterName || null,
              })),
              deleteWasteLineNumbers: deletedWasteLineNumbers,
              deleteTransporterLineNumbers: deletedTransporterLineNumbers,
              markReviewed: needsReview,
            },
          },
        });
        alerts.success("Document manifest data saved. Processing queued.");
        setDeletedWasteLineNumbers([]);
        setDeletedTransporterLineNumbers([]);
        onClose();
      } catch (err) {
        alerts.error(
          "Failed to save document manifest data.",
          err instanceof Error ? err : undefined
        );
      }
    },
    [
      editMutation,
      wasteManifestId,
      alerts,
      onClose,
      deletedWasteLineNumbers,
      deletedTransporterLineNumbers,
      needsReview,
    ]
  );

  return (
    <DocumentDialogContainer
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      sx={{
        "& .MuiDialog-paper": {
          height: "960px",
        },
      }}
      title={needsReview ? "Review & Confirm Extraction Data" : "Document Extraction Data"}
      previewUrl={previewUrl}
      fileExtension="pdf"
      formContainerSx={{ minWidth: 0, maxHeight: "calc(960px - 140px)", pl: 1.5 }}
      primaryActionButtonProps={{
        onClick: handleSubmit(onSubmit),
        saveText: needsReview ? "Confirm & Save" : "Save",
        loading: saving,
        disabled: loading,
      }}
      cancelButtonProps={{
        text: needsReview ? "Close Without Reviewing" : "Cancel",
        onClick: onClose,
      }}
    >
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={3}>
          <Alert severity="info" variant="outlined">
            {needsReview
              ? "We extracted data from this manifest document. Please review the AI-parsed values (marked with ✨) and confirm."
              : "This is the raw data extracted from the manifest document. Edits here will update the waste manifest record and trigger reprocessing."}
          </Alert>

          {/* Manifest-level fields */}
          <Typography variant="subtitle1" fontWeight="bold">
            Manifest
          </Typography>
          <Stack direction="row" spacing={2}>
            <SuggestedTextField
              name="manifestTrackingNumber"
              control={control}
              needsReview={needsReview}
              label="Tracking Number"
              size="small"
              fullWidth
            />
            <SuggestedTextField
              name="shippedDate"
              control={control}
              needsReview={needsReview}
              label="Shipped Date"
              type="date"
              size="small"
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <SuggestedTextField
              name="receivedDate"
              control={control}
              needsReview={needsReview}
              label="Received Date"
              type="date"
              size="small"
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Stack>
          {trackingNumberChanged && (
            <Alert severity="warning" variant="outlined">
              Changing the tracking number will re-associate this manifest data with a different
              processed manifest record. A re-parse of the source document will create a new record
              under the original tracking number.
            </Alert>
          )}

          {/* Generator */}
          <Typography variant="subtitle1" fontWeight="bold">
            Generator
          </Typography>
          <Stack direction="row" spacing={2}>
            <SuggestedTextField
              name="generatorId"
              control={control}
              needsReview={needsReview}
              label="EPA ID"
              size="small"
              fullWidth
            />
            <SuggestedTextField
              name="generatorName"
              control={control}
              needsReview={needsReview}
              label="Name"
              size="small"
              fullWidth
            />
          </Stack>
          <Typography variant="subtitle2" color="text.secondary">
            Mailing Address
          </Typography>
          <Stack direction="row" spacing={2}>
            <SuggestedTextField
              name="generatorMailStreet1"
              control={control}
              needsReview={needsReview}
              label="Mailing Street 1"
              size="small"
              fullWidth
            />
            <SuggestedTextField
              name="generatorMailStreet2"
              control={control}
              needsReview={needsReview}
              label="Mailing Street 2"
              size="small"
              fullWidth
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <SuggestedTextField
              name="generatorMailCity"
              control={control}
              needsReview={needsReview}
              label="City"
              size="small"
              fullWidth
            />
            <SuggestedTextField
              name="generatorMailState"
              control={control}
              needsReview={needsReview}
              label="State"
              size="small"
              sx={{ maxWidth: 100 }}
            />
            <SuggestedTextField
              name="generatorMailZip"
              control={control}
              needsReview={needsReview}
              label="Zip"
              size="small"
              sx={{ maxWidth: 120 }}
            />
          </Stack>

          <Typography variant="subtitle2" color="text.secondary">
            Site Address
          </Typography>
          <Stack direction="row" spacing={2}>
            <SuggestedTextField
              name="generatorLocationStreet1"
              control={control}
              needsReview={needsReview}
              label="Street 1"
              size="small"
              fullWidth
            />
            <SuggestedTextField
              name="generatorLocationStreet2"
              control={control}
              needsReview={needsReview}
              label="Street 2"
              size="small"
              fullWidth
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <SuggestedTextField
              name="generatorLocationCity"
              control={control}
              needsReview={needsReview}
              label="City"
              size="small"
              fullWidth
            />
            <SuggestedTextField
              name="generatorLocationState"
              control={control}
              needsReview={needsReview}
              label="State"
              size="small"
              sx={{ maxWidth: 100 }}
            />
            <SuggestedTextField
              name="generatorLocationZip"
              control={control}
              needsReview={needsReview}
              label="Zip"
              size="small"
              sx={{ maxWidth: 120 }}
            />
          </Stack>

          {/* Destination Facility */}
          <Typography variant="subtitle1" fontWeight="bold">
            Destination Facility (TSDF)
          </Typography>
          <Stack direction="row" spacing={2}>
            <SuggestedTextField
              name="desFacilityId"
              control={control}
              needsReview={needsReview}
              label="EPA ID"
              size="small"
              fullWidth
            />
            <SuggestedTextField
              name="desFacilityName"
              control={control}
              needsReview={needsReview}
              label="Name"
              size="small"
              fullWidth
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <SuggestedTextField
              name="desFacLocationStreet1"
              control={control}
              needsReview={needsReview}
              label="Street"
              size="small"
              fullWidth
            />
            <SuggestedTextField
              name="desFacLocationCity"
              control={control}
              needsReview={needsReview}
              label="City"
              size="small"
              fullWidth
            />
            <SuggestedTextField
              name="desFacLocationState"
              control={control}
              needsReview={needsReview}
              label="State"
              size="small"
              sx={{ maxWidth: 100 }}
            />
            <SuggestedTextField
              name="desFacLocationZip"
              control={control}
              needsReview={needsReview}
              label="Zip"
              size="small"
              sx={{ maxWidth: 120 }}
            />
          </Stack>

          {/* Waste Lines */}
          <Typography variant="subtitle1" fontWeight="bold">
            Waste Lines ({wasteLineFields.length})
          </Typography>
          {wasteLineFields.map((field, index) => (
            <Accordion key={field.id} defaultExpanded={index === 0}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Stack direction="row" alignItems="center" spacing={1} flex={1}>
                  <Typography variant="subtitle2">Line {field.wasteLineNumber}</Typography>
                  <Box flex={1} />
                  <IconButton
                    component="div"
                    role="button"
                    tabIndex={0}
                    size="small"
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      setDeletedWasteLineNumbers((prev) => [...prev, field.wasteLineNumber]);
                      removeWasteLine(index);
                    }}
                    title="Remove waste line"
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <SuggestedField
                      name={`wasteLines.${index}.dotHazardous`}
                      control={control}
                      needsReview={needsReview}
                    >
                      {({ field, isSuggested, sparkleIcon }) => (
                        <Stack direction="row" alignItems="center">
                          <FormControlLabel
                            control={<Checkbox checked={!!field.value} onChange={field.onChange} />}
                            label="DOT Hazardous"
                          />
                          {isSuggested && sparkleIcon}
                        </Stack>
                      )}
                    </SuggestedField>
                    <SuggestedTextField
                      name={`wasteLines.${index}.dotIdNumberDescription`}
                      control={control}
                      needsReview={needsReview}
                      label="DOT ID/Description"
                      size="small"
                      fullWidth
                    />
                  </Stack>
                  <SuggestedTextField
                    name={`wasteLines.${index}.dotPrintedInformation`}
                    control={control}
                    needsReview={needsReview}
                    label="DOT Printed Information"
                    size="small"
                    fullWidth
                  />
                  <SuggestedTextField
                    name={`wasteLines.${index}.nonHazWasteDescription`}
                    control={control}
                    needsReview={needsReview}
                    label="Non-Haz Waste Description"
                    size="small"
                    fullWidth
                  />
                  <Stack direction="row" spacing={2}>
                    <SuggestedField
                      name={`wasteLines.${index}.containerNumber`}
                      control={control}
                      needsReview={needsReview}
                    >
                      {({ field, isSuggested, sparkleAdornment }) => (
                        <TextField
                          {...field}
                          value={field.value ?? ""}
                          onChange={(e) =>
                            field.onChange(
                              e.target.value === "" ? null : safeParseInt(e.target.value)
                            )
                          }
                          label="Container #"
                          type="number"
                          size="small"
                          sx={{ maxWidth: 120 }}
                          slotProps={{
                            input: { startAdornment: isSuggested ? sparkleAdornment : undefined },
                          }}
                        />
                      )}
                    </SuggestedField>
                    <SuggestedTextField
                      name={`wasteLines.${index}.containerTypeCode`}
                      control={control}
                      needsReview={needsReview}
                      label="Container Type"
                      size="small"
                      sx={{ maxWidth: 150 }}
                    />
                    <SuggestedField
                      name={`wasteLines.${index}.quantity`}
                      control={control}
                      needsReview={needsReview}
                    >
                      {({ field, isSuggested, sparkleAdornment }) => (
                        <TextField
                          {...field}
                          value={field.value ?? ""}
                          onChange={(e) =>
                            field.onChange(
                              e.target.value === "" ? null : safeParseFloat(e.target.value)
                            )
                          }
                          label="Quantity"
                          type="number"
                          size="small"
                          sx={{ maxWidth: 150 }}
                          slotProps={{
                            input: { startAdornment: isSuggested ? sparkleAdornment : undefined },
                          }}
                        />
                      )}
                    </SuggestedField>
                    <SuggestedTextField
                      name={`wasteLines.${index}.qtyUnitOfMeasureCode`}
                      control={control}
                      needsReview={needsReview}
                      label="UOM Code"
                      size="small"
                      sx={{ maxWidth: 120 }}
                    />
                    <SuggestedTextField
                      name={`wasteLines.${index}.managementMethodCode`}
                      control={control}
                      needsReview={needsReview}
                      label="Mgmt Method"
                      size="small"
                      sx={{ maxWidth: 150 }}
                    />
                  </Stack>
                  <Stack direction="row" spacing={2}>
                    <SuggestedTextField
                      name={`wasteLines.${index}.federalWasteCodes`}
                      control={control}
                      needsReview={needsReview}
                      label="Federal Waste Codes"
                      size="small"
                      fullWidth
                      helperText="Comma-separated"
                    />
                    <SuggestedTextField
                      name={`wasteLines.${index}.stateWasteCodes`}
                      control={control}
                      needsReview={needsReview}
                      label="State Waste Codes"
                      size="small"
                      fullWidth
                      helperText="Comma-separated"
                    />
                  </Stack>
                </Stack>
              </AccordionDetails>
            </Accordion>
          ))}
          <Button
            size="small"
            startIcon={<Add />}
            onClick={() => {
              const nextLineNumber = String(
                Math.max(0, ...wasteLineFields.map((f) => safeParseInt(f.wasteLineNumber) ?? 0)) + 1
              );
              appendWasteLine({
                wasteLineNumber: nextLineNumber,
                dotHazardous: false,
                dotIdNumberDescription: "",
                dotPrintedInformation: "",
                nonHazWasteDescription: "",
                containerNumber: null,
                containerTypeCode: "",
                quantity: null,
                qtyUnitOfMeasureCode: "",
                managementMethodCode: "",
                federalWasteCodes: "",
                stateWasteCodes: "",
              });
            }}
          >
            Add Waste Line
          </Button>

          {/* Transporters */}
          <Typography variant="subtitle1" fontWeight="bold">
            Transporters ({transporterFields.length})
          </Typography>
          {transporterFields.map((field, index) => (
            <Stack key={field.id} direction="row" spacing={2} alignItems="center">
              <Typography variant="body2">{field.transporterLineNumber}.</Typography>
              <SuggestedTextField
                name={`transporters.${index}.transporterId`}
                control={control}
                needsReview={needsReview}
                label="EPA ID"
                size="small"
                sx={{ minWidth: 150 }}
              />
              <SuggestedTextField
                name={`transporters.${index}.transporterName`}
                control={control}
                needsReview={needsReview}
                label="Name"
                size="small"
                fullWidth
              />
              <IconButton
                size="small"
                onClick={() => {
                  setDeletedTransporterLineNumbers((prev) => [
                    ...prev,
                    field.transporterLineNumber,
                  ]);
                  removeTransporter(index);
                }}
                title="Remove transporter"
              >
                <Close fontSize="small" />
              </IconButton>
            </Stack>
          ))}
          <Button
            size="small"
            startIcon={<Add />}
            onClick={() => {
              const nextLineNumber = String(
                Math.max(
                  0,
                  ...transporterFields.map((f) => safeParseInt(f.transporterLineNumber) ?? 0)
                ) + 1
              );
              appendTransporter({
                transporterLineNumber: nextLineNumber,
                transporterId: "",
                transporterName: "",
              });
            }}
          >
            Add Transporter
          </Button>
        </Stack>
      )}
    </DocumentDialogContainer>
  );
}
